/**
 * hb-nav.js
 * Hanan Bakri: the one navigation behaviour the CSS cannot express.
 *
 * 1. ESCAPE CLOSES THE SHEET. Horizon's <header-menu> opens the mega menu on
 *    hover and on focus and closes it on pointerleave and on blur. It has no
 *    Escape handler, so a keyboard visitor who opened a sheet could only
 *    close it by tabbing all the way through it. Escape now closes it and
 *    returns the focus to the label that opened it.
 *
 *    It calls Horizon's own public deactivate() rather than reaching into
 *    the component's state, so nothing here knows how the sheet is built.
 *
 * 2. THE FILM PLAYS ON OPEN AND PAUSES ON CLOSE. hb-plate-video.js drives
 *    the films in the page's bands from an IntersectionObserver, which
 *    cannot express "the menu opened": the sheet is in the header at every
 *    scroll position, so an observer would report it visible and fetch the
 *    file on every page load for a menu nobody opened. Same play() rules -
 *    preload flipped off none at the first play, muted set as a PROPERTY as
 *    well as an attribute because Safari declines a video whose muted state
 *    it decides was set too late, and nothing at all under reduced motion,
 *    where the poster is a frame of the film and stands in its place.
 *
 * Safety posture, matching hb-motion.js:
 * - Nothing here hides anything, so if this file is blocked or throws, the
 *   menu behaves exactly as Horizon ships it and the poster stands.
 * - Every step is guarded; a Horizon update that renames deactivate() costs
 *   the Escape key, not the navigation.
 *
 * Dependency free. No globals.
 */

(function () {
  'use strict';

  var reduce = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false, addEventListener: null };

  function openTrigger() {
    return document.querySelector('#header-group .menu-list__link[aria-expanded="true"]');
  }

  /* ------------------------------------------------------------------ film */

  function films(scope) {
    return (scope || document).querySelectorAll('[data-hb-menu-video]');
  }

  /* The poster is carried as data-poster so a closed panel never fetches it.
     A browser downloads a video's poster even under preload="none", because
     the poster is the thing it paints, and on a throttled phone this one was
     the first image request on the home page - ahead of the hero - for a
     sheet nobody had opened. Promote it the moment the sheet opens, once. */
  function promotePoster(video) {
    var src = video.getAttribute('data-poster');
    if (!src || video.getAttribute('poster')) return;
    video.setAttribute('poster', src);
  }

  function play(video) {
    promotePoster(video);
    if (reduce.matches) return;
    video.muted = true;
    video.defaultMuted = true;
    /* preload="none" means the first play() is also the fetch. */
    if (video.preload === 'none') video.preload = 'auto';
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  }

  function stop(video) {
    if (video.paused) return;
    video.pause();
  }

  function syncFilms() {
    var trigger = openTrigger();
    var openItem = trigger ? trigger.closest('.menu-list__list-item') : null;

    Array.prototype.forEach.call(films(document), function (video) {
      var inOpenSheet = openItem !== null && openItem.contains(video);
      if (inOpenSheet) {
        /* Under reduced motion play() returns before starting, and then the
           poster is the only thing the panel has to show - so it is promoted
           on the open path, not inside the play path. */
        promotePoster(video);
        play(video);
      } else {
        stop(video);
      }
    });
  }

  /* aria-expanded is the only signal Horizon publishes for open and close,
     and it writes it from both the pointer and the focus paths, so watching
     the attribute catches every way in and every way out. */
  function watchMenus() {
    var menus = document.querySelectorAll('#header-group header-menu');
    if (!menus.length || !window.MutationObserver) return;

    var observer = new MutationObserver(function () {
      syncFilms();
    });

    Array.prototype.forEach.call(menus, function (menu) {
      observer.observe(menu, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchMenus);
  } else {
    watchMenus();
  }

  document.addEventListener('shopify:section:load', watchMenus);

  /* A reader who turns the preference on mid-visit should not have to
     reload to be left alone. */
  if (reduce.addEventListener) {
    reduce.addEventListener('change', function () {
      if (!reduce.matches) return;
      Array.prototype.forEach.call(films(document), stop);
    });
  }

  /* --------------------------------------------------------------- escape */

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;

      var trigger = openTrigger();
      if (!trigger) return;

      var menu = trigger.closest('header-menu');
      if (!menu || typeof menu.deactivate !== 'function') return;

      /* Horizon's deactivate() reads target, relatedTarget and type off the
         event and closes when the focus is not travelling into the sheet.
         A plain object carrying those three is all it looks at. */
      try {
        menu.deactivate({ target: trigger, relatedTarget: null, type: 'keydown' });
      } catch (e) {
        return;
      }

      /* Focus goes back to the label that opened the sheet, not to the top
         of the document. */
      if (typeof trigger.focus === 'function') {
        trigger.focus({ preventScroll: true });
      }

      event.stopPropagation();
    },
    true
  );
})();
